import { ComponentType, lazy as reactLazy } from "react";

/**
 * Drop-in replacement for React.lazy. On ChunkLoadError (stale bundle after deploy),
 * reloads the page once so the browser fetches fresh index.html and chunk hashes.
 * Further failures within the TTL fall through to the route ErrorBoundary.
 */
export function lazy<T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>
) {
  return reactLazy(async () => {
    try {
      return await importer();
    } catch (error) {
      if (
        isChunkLoadError(error) &&
        shouldAttemptChunkReload() &&
        markChunkReloadAttempted()
      ) {
        console.log("chunk load error, reloading");
        window.location.reload();
        return new Promise(() => {});
      }
      throw error;
    }
  });
}

const CHUNK_RELOAD_KEY = "chunk_reload";
const CHUNK_RELOAD_TTL_MS = 1 * 60 * 1000; // 1-minute TTL for chunk reload to not reload loop

/** True when webpack/CRA reports a missing or failed code-split chunk. */
function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.name === "ChunkLoadError" ||
    /Loading chunk [\d]+ failed/.test(error.message)
  );
}

/** One auto-reload per tab per TTL window; skips when sessionStorage is unavailable. */
function shouldAttemptChunkReload(): boolean {
  try {
    if (typeof sessionStorage === "undefined") return false;
    const raw = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    if (!raw) return true;
    const { at } = JSON.parse(raw) as { at?: number };
    return !at || Date.now() - at > CHUNK_RELOAD_TTL_MS;
  } catch {
    return false;
  }
}

/** Records reload attempt timestamp; returns false if storage write fails. */
function markChunkReloadAttempted(): boolean {
  try {
    sessionStorage.setItem(
      CHUNK_RELOAD_KEY,
      JSON.stringify({ at: Date.now() })
    );
    return true;
  } catch {
    return false;
  }
}
